import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import {
  ShoppingCart,
  Search,
  User,
  Heart,
  Star,
  ChevronRight,
  Plus,
  Minus,
  X,
  Check,
  Truck,
  Shield,
  RefreshCcw,
  Instagram,
  Facebook,
  Twitter,
  Menu,
  CreditCard,
  Loader2,
  CheckCircle,
  XCircle,
  ArrowLeft } from
'lucide-react';

const API_URL = API_BASE;

export const PublicStorefront = () => {const { t } = useTranslation("ecommerce");
  const { storeSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [store, setStore] = useState(null);
  const [theme, setTheme] = useState({});
  const [products, setProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Checkout state
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    email: '',
    name: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });

  // Payment status state (for after redirect)
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentChecking, setPaymentChecking] = useState(false);

  useEffect(() => {
    fetchStoreData();
  }, [storeSlug]);

  // Check for payment status on page load (after redirect from Stripe)
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    setPaymentChecking(true);
    try {
      // Poll for payment status
      const pollStatus = async (attempts = 0) => {
        if (attempts >= 10) {
          setPaymentStatus({ status: 'timeout', message: 'Payment verification timeout' });
          setPaymentChecking(false);
          return;
        }

        const res = await fetch(`${API_URL}/api/ecommerce/checkout/status/${sessionId}`);
        if (res.ok) {
          const data = await res.json();

          if (data.payment_status === 'paid' || data.status === 'complete') {
            setPaymentStatus({
              status: 'success',
              message: 'Payment successful!',
              orderId: data.order_id
            });
            setCart([]); // Clear cart after successful payment
            setPaymentChecking(false);
          } else if (data.status === 'expired') {
            setPaymentStatus({ status: 'expired', message: 'Payment session expired' });
            setPaymentChecking(false);
          } else {
            // Still pending, continue polling
            setTimeout(() => pollStatus(attempts + 1), 2000);
          }
        } else {
          setTimeout(() => pollStatus(attempts + 1), 2000);
        }
      };

      pollStatus();
    } catch (err) {
      setPaymentStatus({ status: 'error', message: 'Failed to verify payment' });
      setPaymentChecking(false);
    }
  };

  const fetchStoreData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ecommerce/public/store/${storeSlug}`);
      if (res.ok) {
        const data = await res.json();
        setStore(data.store);
        setTheme(data.theme || {});
        setProducts(data.products || []);
        setFeaturedProducts(data.featured_products || []);
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Failed to load store');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    const existing = cart.find((item) => item.id === product.id);
    if (existing) {
      setCart(cart.map((item) =>
      item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter((item) => item.id !== productId));
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map((item) => {
      if (item.id === productId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter((item) => item.quantity > 0));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.sale_price || item.price) * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const filteredProducts = selectedCategory === 'all' ?
  products :
  products.filter((p) => p.category === selectedCategory);

  // Handle checkout - create Stripe session and redirect
  const handleCheckout = async () => {
    if (cart.length === 0) return;

    setCheckoutLoading(true);
    try {
      const cartItems = cart.map((item) => ({
        product_id: item.id,
        name: item.name,
        price: item.sale_price || item.price,
        quantity: item.quantity,
        image: item.image
      }));

      const response = await fetch(`${API_URL}/api/ecommerce/checkout/create-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          store_id: store?.id || 1,
          cart_items: cartItems,
          customer_email: checkoutForm.email || undefined,
          customer_name: checkoutForm.name || undefined,
          shipping_address: checkoutForm.address ? {
            line1: checkoutForm.address,
            city: checkoutForm.city,
            state: checkoutForm.state,
            postal_code: checkoutForm.zip,
            country: 'BR'
          } : undefined,
          currency: 'brl'
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        alert('Checkout failed: ' + (error.detail || 'Unknown error'));
      }
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.background_color || '#FDFBF7' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: theme.primary_color || '#8B9A6D' }} />
      </div>);

  }

  if (!store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">{t("storeNotFound", "Store Not Found")}</h1>
          <p className="text-gray-600">{t("theStoreYoure", "The store you're looking for doesn't exist or is not published.")}</p>
        </div>
      </div>);

  }

  // Dynamic styles based on theme
  const styles = {
    '--primary': theme.primary_color || '#8B9A6D',
    '--secondary': theme.secondary_color || '#D97B3B',
    '--background': theme.background_color || '#FDFBF7',
    '--text': theme.text_color || '#2D3B2D',
    '--accent': theme.accent_color || '#E8DFD0'
  };

  return (
    <div className="min-h-screen" style={{ ...styles, backgroundColor: 'var(--background)', color: 'var(--text)' }}>
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg bg-white/80 border-b" style={{ borderColor: 'var(--accent)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              {store.logo_url ?
              <img src={store.logo_url} alt={store.name} className="h-10 w-10 rounded-lg object-cover" /> :

              <div className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: 'var(--primary)' }}>
                  {store.name?.charAt(0)}
                </div>
              }
              <span className="text-xl font-semibold" style={{ fontFamily: theme.font_heading || 'Cormorant Garamond, serif' }}>
                {store.name}
              </span>
            </div>

            {/* Nav Links */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#" className="text-sm font-medium hover:opacity-70 transition-opacity">{t("home", "Home")}</a>
              <a href="#products" className="text-sm font-medium hover:opacity-70 transition-opacity">{t("shop", "Shop")}</a>
              <a href="#" className="text-sm font-medium hover:opacity-70 transition-opacity">{t("about", "About")}</a>
              <a href="#" className="text-sm font-medium hover:opacity-70 transition-opacity">{t("contact", "Contact")}</a>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4">
              <button className="p-2 hover:opacity-70 transition-opacity">
                <Search className="h-5 w-5" />
              </button>
              <button className="p-2 hover:opacity-70 transition-opacity">
                <Heart className="h-5 w-5" />
              </button>
              <button
                className="p-2 hover:opacity-70 transition-opacity relative"
                onClick={() => setShowCart(true)}>
                
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 &&
                <span
                  className="absolute -top-1 -right-1 h-5 w-5 rounded-full text-xs flex items-center justify-center text-white"
                  style={{ backgroundColor: 'var(--secondary)' }}>
                  
                    {cartCount}
                  </span>
                }
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-32">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 30% 50%, var(--primary) 0%, transparent 50%), radial-gradient(circle at 70% 80%, var(--secondary) 0%, transparent 40%)`
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span
                className="inline-block px-4 py-1 rounded-full text-sm mb-6"
                style={{ backgroundColor: 'var(--accent)', color: 'var(--primary)' }}>{t("newCollection", "\u2728 New Collection")}


              </span>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6"
                style={{ fontFamily: theme.font_heading || 'Cormorant Garamond, serif' }}>{t("naturalBeauty", "Natural Beauty,")}

                <br />
                <span style={{ color: 'var(--primary)' }}>{t("pureResults", "Pure Results")}</span>
              </h1>
              <p className="text-lg opacity-80 mb-8 max-w-lg" style={{ fontFamily: theme.font_body || 'Lato, sans-serif' }}>
                {store.description || 'Discover our carefully curated collection of premium skincare products designed to enhance your natural beauty.'}
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#products"
                  className="px-8 py-3 rounded-full text-white font-medium transition-transform hover:scale-105"
                  style={{ backgroundColor: 'var(--primary)' }}>{t("shopNow", "Shop Now")}


                </a>
                <button
                  className="px-8 py-3 rounded-full font-medium border-2 transition-transform hover:scale-105"
                  style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}>{t("learnMore", "Learn More")}


                </button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={featuredProducts[0]?.image || 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=600&h=600&fit=crop'}
                  alt="Featured Product"
                  className="w-full h-full object-cover" />
                
              </div>
              {/* Floating Badge */}
              <div
                className="absolute -bottom-4 -left-4 px-6 py-3 rounded-2xl shadow-xl"
                style={{ backgroundColor: 'white' }}>
                
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    {[...Array(3)].map((_, i) =>
                    <div key={i} className="w-8 h-8 rounded-full border-2 border-white overflow-hidden">
                        <img
                        src={`https://i.pravatar.cc/100?img=${i + 10}`}
                        alt=""
                        className="w-full h-full object-cover" />
                      
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) =>
                      <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />
                      )}
                    </div>
                    <p className="text-xs text-gray-600">{t("500HappyCustomers", "500+ Happy Customers")}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-12 border-y" style={{ borderColor: 'var(--accent)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
            { icon: Truck, title: 'Free Shipping', desc: 'On orders over R$200' },
            { icon: Shield, title: 'Secure Payment', desc: '100% secure checkout' },
            { icon: RefreshCcw, title: 'Easy Returns', desc: '30-day return policy' },
            { icon: Check, title: 'Quality Assured', desc: 'Premium ingredients' }].
            map((feature, idx) =>
            <div key={idx} className="flex items-center gap-3">
                <div className="p-3 rounded-xl" style={{ backgroundColor: 'var(--accent)' }}>
                  <feature.icon className="h-5 w-5" style={{ color: 'var(--primary)' }} />
                </div>
                <div>
                  <p className="font-medium text-sm">{feature.title}</p>
                  <p className="text-xs opacity-70">{feature.desc}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2
              className="text-3xl font-bold mb-4"
              style={{ fontFamily: theme.font_heading || 'Cormorant Garamond, serif' }}>{t("shopByCategory", "Shop by Category")}


            </h2>
            <p className="opacity-70">{t("findThePerfect", "Find the perfect products for your skincare routine")}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
              selectedCategory === 'all' ? 'text-white' : ''}`
              }
              style={{
                backgroundColor: selectedCategory === 'all' ? 'var(--primary)' : 'var(--accent)',
                color: selectedCategory === 'all' ? 'white' : 'var(--text)'
              }}>{t("allProducts", "All Products")}


            </button>
            {categories.map((cat) =>
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
              selectedCategory === cat ? 'text-white' : ''}`
              }
              style={{
                backgroundColor: selectedCategory === cat ? 'var(--primary)' : 'var(--accent)',
                color: selectedCategory === cat ? 'white' : 'var(--text)'
              }}>
              
                {cat}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section id="products" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredProducts.map((product) =>
            <div
              key={product.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300">
              
                {/* Product Image */}
                <div className="relative aspect-square overflow-hidden">
                  <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                
                  {product.sale_price &&
                <span
                  className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: 'var(--secondary)' }}>{t("sale", "Sale")}


                </span>
                }
                  {product.is_featured &&
                <span
                  className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: 'var(--primary)' }}>{t("featured", "\u2605 Featured")}


                </span>
                }
                  {/* Quick Add */}
                  <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                    onClick={() => addToCart(product)}
                    className="w-full py-2 rounded-full text-sm font-medium text-white transition-transform hover:scale-105"
                    style={{ backgroundColor: 'var(--primary)' }}>{t("addToCart", "Add to Cart")}


                  </button>
                  </div>
                </div>
                {/* Product Info */}
                <div className="p-4">
                  <p className="text-xs opacity-60 mb-1">{product.category}</p>
                  <h3 className="font-medium mb-2 line-clamp-2">{product.name}</h3>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex">
                      {[...Array(5)].map((_, i) =>
                    <Star
                      key={i}
                      className={`h-3 w-3 ${i < Math.floor(product.rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />

                    )}
                    </div>
                    <span className="text-xs opacity-60">({product.reviews})</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    {product.sale_price ?
                  <>
                        <span className="font-bold" style={{ color: 'var(--secondary)' }}>
                          {formatCurrency(product.sale_price)}
                        </span>
                        <span className="text-sm opacity-50 line-through">
                          {formatCurrency(product.price)}
                        </span>
                      </> :

                  <span className="font-bold" style={{ color: 'var(--primary)' }}>
                        {formatCurrency(product.price)}
                      </span>
                  }
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20" style={{ backgroundColor: 'var(--accent)' }}>
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2
            className="text-3xl font-bold mb-4"
            style={{ fontFamily: theme.font_heading || 'Cormorant Garamond, serif' }}>{t("joinOurNewsletter", "Join Our Newsletter")}


          </h2>
          <p className="opacity-70 mb-8">{t("subscribeToGet", "Subscribe to get special offers, free giveaways, and updates on new products.")}

          </p>
          <div className="flex gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-6 py-3 rounded-full border focus:outline-none focus:ring-2"
              style={{ borderColor: 'var(--primary)', focusRingColor: 'var(--primary)' }} />
            
            <button
              className="px-8 py-3 rounded-full text-white font-medium"
              style={{ backgroundColor: 'var(--primary)' }}>{t("subscribe", "Subscribe")}


            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t" style={{ borderColor: 'var(--accent)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                {store.logo_url ?
                <img src={store.logo_url} alt={store.name} className="h-8 w-8 rounded-lg object-cover" /> :

                <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: 'var(--primary)' }}>
                    {store.name?.charAt(0)}
                  </div>
                }
                <span className="font-semibold">{store.name}</span>
              </div>
              <p className="text-sm opacity-70 mb-4">{t("premiumSkincareProducts", "Premium skincare products for naturally beautiful skin.")}

              </p>
              <div className="flex gap-4">
                <a href="#" className="opacity-60 hover:opacity-100 transition-opacity">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="#" className="opacity-60 hover:opacity-100 transition-opacity">
                  <Facebook className="h-5 w-5" />
                </a>
                <a href="#" className="opacity-60 hover:opacity-100 transition-opacity">
                  <Twitter className="h-5 w-5" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t("shop", "Shop")}</h4>
              <ul className="space-y-2 text-sm opacity-70">
                <li><a href="#" className="hover:opacity-100">{t("allProducts", "All Products")}</a></li>
                <li><a href="#" className="hover:opacity-100">{t("newArrivals", "New Arrivals")}</a></li>
                <li><a href="#" className="hover:opacity-100">{t("bestSellers", "Best Sellers")}</a></li>
                <li><a href="#" className="hover:opacity-100">{t("sale", "Sale")}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t("support", "Support")}</h4>
              <ul className="space-y-2 text-sm opacity-70">
                <li><a href="#" className="hover:opacity-100">{t("contactUs", "Contact Us")}</a></li>
                <li><a href="#" className="hover:opacity-100">{t("faqs", "FAQs")}</a></li>
                <li><a href="#" className="hover:opacity-100">{t("shippingInfo", "Shipping Info")}</a></li>
                <li><a href="#" className="hover:opacity-100">{t("returns", "Returns")}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">{t("company", "Company")}</h4>
              <ul className="space-y-2 text-sm opacity-70">
                <li><a href="#" className="hover:opacity-100">{t("aboutUs", "About Us")}</a></li>
                <li><a href="#" className="hover:opacity-100">{t("ourStory", "Our Story")}</a></li>
                <li><a href="#" className="hover:opacity-100">{t("blog", "Blog")}</a></li>
                <li><a href="#" className="hover:opacity-100">{t("press", "Press")}</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t mt-12 pt-8 text-center text-sm opacity-60" style={{ borderColor: 'var(--accent)' }}>
            <p>{t("2024", "\xA9 2024")}{store.name}{t("allRightsReserved", ". All rights reserved. Powered by EzERP")}</p>
          </div>
        </div>
      </footer>

      {/* Cart Sidebar */}
      {showCart &&
      <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCart(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl">
            <div className="flex flex-col h-full">
              {/* Cart Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-semibold">{t("yourCart", "Your Cart (")}{cartCount})</h2>
                <button onClick={() => setShowCart(false)} className="p-2 hover:bg-gray-100 rounded-full">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-6">
                {cart.length === 0 ?
              <div className="text-center py-12">
                    <ShoppingCart className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">{t("yourCartIs", "Your cart is empty")}</p>
                  </div> :

              <div className="space-y-4">
                    {cart.map((item) =>
                <div key={item.id} className="flex gap-4 p-4 bg-gray-50 rounded-xl">
                        <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-lg" />
                  
                        <div className="flex-1">
                          <h3 className="font-medium text-sm">{item.name}</h3>
                          <p className="text-sm font-bold mt-1" style={{ color: 'var(--primary)' }}>
                            {formatCurrency(item.sale_price || item.price)}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            <button
                        onClick={() => updateQuantity(item.id, -1)}
                        className="p-1 rounded-full border">
                        
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                            <button
                        onClick={() => updateQuantity(item.id, 1)}
                        className="p-1 rounded-full border">
                        
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                        <button
                    onClick={() => removeFromCart(item.id)}
                    className="p-2 hover:bg-gray-200 rounded-full self-start">
                    
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                )}
                  </div>
              }
              </div>

              {/* Cart Footer */}
              {cart.length > 0 &&
            <div className="p-6 border-t">
                  <div className="flex justify-between mb-4">
                    <span className="font-medium">{t("subtotal", "Subtotal")}</span>
                    <span className="font-bold text-lg">{formatCurrency(cartTotal)}</span>
                  </div>
                  
                  {!showCheckout ?
              <>
                      <button
                  onClick={() => setShowCheckout(true)}
                  className="w-full py-3 rounded-full text-white font-medium flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
                  style={{ backgroundColor: 'var(--primary)' }}>
                  
                        <CreditCard className="h-5 w-5" />{t("proceedToCheckout", "Proceed to Checkout")}

                </button>
                      <p className="text-xs text-center mt-3 opacity-60">{t("secureCheckoutPowered", "Secure checkout powered by Stripe")}

                </p>
                    </> :

              <div className="space-y-3">
                      <div>
                        <input
                    type="email"
                    placeholder="Email address *"
                    value={checkoutForm.email}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, email: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:outline-none"
                    style={{ focusRingColor: 'var(--primary)' }} />
                  
                      </div>
                      <div>
                        <input
                    type="text"
                    placeholder="Full name"
                    value={checkoutForm.name}
                    onChange={(e) => setCheckoutForm({ ...checkoutForm, name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:outline-none" />
                  
                      </div>
                      <button
                  onClick={handleCheckout}
                  disabled={checkoutLoading || !checkoutForm.email}
                  className="w-full py-3 rounded-full text-white font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition-transform hover:scale-[1.02]"
                  style={{ backgroundColor: 'var(--primary)' }}>
                  
                        {checkoutLoading ?
                  <>
                            <Loader2 className="h-5 w-5 animate-spin" />{t("processing", "Processing...")}

                  </> :

                  <>
                            <Shield className="h-5 w-5" />{t("pay", "Pay")}
                    {formatCurrency(cartTotal)}
                          </>
                  }
                      </button>
                      <button
                  onClick={() => setShowCheckout(false)}
                  className="w-full py-2 text-sm text-gray-500 hover:text-gray-700">{t("backToCart", "\u2190 Back to cart")}


                </button>
                    </div>
              }
                </div>
            }
            </div>
          </div>
        </div>
      }

      {/* Payment Status Modal */}
      {(paymentStatus || paymentChecking) &&
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => !paymentChecking && setPaymentStatus(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            {paymentChecking ?
          <>
                <Loader2 className="h-16 w-16 mx-auto mb-4 animate-spin" style={{ color: 'var(--primary)' }} />
                <h2 className="text-xl font-semibold mb-2">{t("verifyingPayment", "Verifying Payment")}</h2>
                <p className="text-gray-500">{t("pleaseWaitWhile", "Please wait while we confirm your payment...")}</p>
              </> :
          paymentStatus?.status === 'success' ?
          <>
                <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--primary)' }}>
                  <CheckCircle className="h-12 w-12 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-2">{t("paymentSuccessful", "Payment Successful!")}</h2>
                <p className="text-gray-500 mb-2">{t("thankYouFor", "Thank you for your order")}</p>
                {paymentStatus.orderId &&
            <p className="text-sm font-mono bg-gray-100 px-4 py-2 rounded-lg inline-block mb-4">{t("order", "Order: #")}
              {paymentStatus.orderId}
                  </p>
            }
                <p className="text-sm text-gray-400 mb-6">{t("aConfirmationEmail", "A confirmation email will be sent shortly.")}

            </p>
                <button
              onClick={() => {
                setPaymentStatus(null);
                window.history.replaceState({}, '', window.location.pathname);
              }}
              className="px-6 py-3 rounded-full text-white font-medium"
              style={{ backgroundColor: 'var(--primary)' }}>{t("continueShopping", "Continue Shopping")}


            </button>
              </> :

          <>
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="h-12 w-12 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold mb-2">
                  {paymentStatus?.status === 'expired' ? 'Session Expired' : 'Payment Issue'}
                </h2>
                <p className="text-gray-500 mb-6">{paymentStatus?.message}</p>
                <button
              onClick={() => {
                setPaymentStatus(null);
                window.history.replaceState({}, '', window.location.pathname);
              }}
              className="px-6 py-3 rounded-full border-2 font-medium"
              style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}>{t("tryAgain", "Try Again")}


            </button>
              </>
          }
          </div>
        </div>
      }
    </div>);

};

export default PublicStorefront;